/**
 * Banner Carrousel
 * Componente de carrossel usando BlazeSlider
 */

class BannerCarrousel extends HTMLElement {
  constructor() {
    super();
    
    this.slider = this.querySelector('.blaze-slider');
    this.videos = this.querySelectorAll('video');
    this.playBtns = this.querySelectorAll('.banner__video-play');
    this.sliderElement = null;
    this.maxRetries = 10;
    this.retryDelay = 100;
    this.retryCount = 0;
    this.autoplayInterval = 5000;
    this.progressBars = [];
    this.currentProgressBar = null;
    this.currentSlideIndex = 0;
    this.progressAnimation = null;
    this.isManualInteraction = false;
    this.touchStartX = null;
    this.touchStartY = null;
    this.autoplayTimer = null;
    this.useManualAutoplay = true; // Control autoplay manually for better restart control

    this.initializeBlazer();
  }

  /**
   * Wait for BlazeSlider to be available with retry mechanism
   */
  waitForBlazeSlider() {
    return new Promise((resolve, reject) => {
      if (typeof BlazeSlider !== 'undefined') {
        resolve();
        return;
      }

      const checkInterval = setInterval(() => {
        this.retryCount++;
        
        if (typeof BlazeSlider !== 'undefined') {
          clearInterval(checkInterval);
          resolve();
        } else if (this.retryCount >= this.maxRetries) {
          clearInterval(checkInterval);
          reject(new Error('BlazeSlider failed to load after maximum retries'));
        }
      }, this.retryDelay);
    });
  }

  async initializeBlazer() {
    if (!this.slider) {
      console.warn('BannerCarrousel: Slider element not found');
      return;
    }

    const slides = this.querySelectorAll('.blaze-slide');
    const slidesCount = slides ? slides.length : 0;

    if (slidesCount <= 1) {
      const prevBtn = this.querySelector('.blaze-prev');
      const nextBtn = this.querySelector('.blaze-next');
      const pagination = this.querySelector('.blaze-pagination-banner-carrousel');
      
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
      if (pagination) pagination.style.display = 'none';
      return;
    }

    try {
      // Wait for BlazeSlider to be available
      await this.waitForBlazeSlider();

      // Initialize BlazeSlider with error handling
      try {
        // Disable BlazeSlider's built-in autoplay if we're using manual control
        this.sliderElement = new BlazeSlider(this.slider, {
          all: {
            transitionDuration: 300,
            slidesToShow: 1,
            slideGap: '0px',
            enableAutoplay: !this.useManualAutoplay, // Disable if using manual control
            autoplayInterval: this.autoplayInterval,
            stopAutoplayOnInteraction: false,
          }
        });

        // Start manual autoplay if enabled
        if (this.useManualAutoplay) {
          this.startManualAutoplay();
        }

        // Initialize progress bars
        this.initializeProgressBars();

        // Setup manual interaction detection (must be after initializeProgressBars)
        this.setupManualInteractionDetection();

        // Handle slide change to remove first-banner class and update progress
        this.sliderElement.onSlide((pageIndex) => {
          const animatedEntry = this.querySelector('.first-banner');
          if (animatedEntry) {
            animatedEntry.classList.remove('first-banner');
          }
          
          // Update progress bar with the correct slide index
          if (pageIndex !== undefined && pageIndex !== null) {
            this.currentSlideIndex = pageIndex;
            
            // If it was manual interaction, restart autoplay and progress bar
            if (this.isManualInteraction) {
              this.restartAutoplayAndProgress();
              this.isManualInteraction = false;
            } else {
              this.updateProgressBar();
            }
          }
        });

        // Start progress bar for initial slide
        setTimeout(() => {
          this.startProgressBar();
        }, 100);

        console.log('BannerCarrousel: Successfully initialized');
      } catch (initError) {
        console.error('BannerCarrousel: Failed to initialize BlazeSlider', initError);
        this.showFallbackUI();
      }
    } catch (loadError) {
      console.error('BannerCarrousel: BlazeSlider library not available', loadError);
      this.showFallbackUI();
    }
  }

  /**
   * Setup manual interaction detection (swipe, drag, button clicks)
   */
  setupManualInteractionDetection() {
    const track = this.querySelector('.blaze-track');
    const prevBtn = this.querySelector('.blaze-prev');
    const nextBtn = this.querySelector('.blaze-next');
    
    if (!track) return;

    // Detect touch/swipe
    track.addEventListener('touchstart', (e) => {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    }, { passive: true });

    track.addEventListener('touchend', (e) => {
      if (this.touchStartX !== null && this.touchStartY !== null) {
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        const diffX = Math.abs(touchEndX - this.touchStartX);
        const diffY = Math.abs(touchEndY - this.touchStartY);
        
        // If horizontal swipe is greater than vertical, it's a swipe
        if (diffX > diffY && diffX > 10) {
          this.isManualInteraction = true;
          // Immediately restart autoplay when swipe detected
          if (this.useManualAutoplay) {
            this.startManualAutoplay();
          }
        }
        
        this.touchStartX = null;
        this.touchStartY = null;
      }
    }, { passive: true });

    // Detect mouse drag
    let isDragging = false;
    let dragStartX = null;

    track.addEventListener('mousedown', (e) => {
      isDragging = true;
      dragStartX = e.clientX;
    });

    track.addEventListener('mousemove', (e) => {
      if (isDragging && dragStartX !== null) {
        const diffX = Math.abs(e.clientX - dragStartX);
        if (diffX > 10) {
          this.isManualInteraction = true;
          // Immediately restart autoplay when drag detected
          if (this.useManualAutoplay) {
            this.startManualAutoplay();
          }
        }
      }
    });

    track.addEventListener('mouseup', () => {
      isDragging = false;
      dragStartX = null;
    });

    track.addEventListener('mouseleave', () => {
      isDragging = false;
      dragStartX = null;
    });

    // Detect button clicks
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        this.isManualInteraction = true;
        // Immediately restart autoplay when button clicked
        if (this.useManualAutoplay) {
          this.startManualAutoplay();
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        this.isManualInteraction = true;
        // Immediately restart autoplay when button clicked
        if (this.useManualAutoplay) {
          this.startManualAutoplay();
        }
      });
    }

    // Detect progress bar clicks (bars are already initialized at this point)
    if (this.progressBars && this.progressBars.length > 0) {
      this.progressBars.forEach((bar) => {
        bar.addEventListener('click', () => {
          this.isManualInteraction = true;
          // Immediately restart autoplay when bar clicked
          if (this.useManualAutoplay) {
            this.startManualAutoplay();
          }
        });
      });
    }
  }

  /**
   * Start manual autoplay control
   */
  startManualAutoplay() {
    this.stopManualAutoplay();
    
    this.autoplayTimer = setInterval(() => {
      if (this.sliderElement && typeof this.sliderElement.next === 'function') {
        this.sliderElement.next();
      }
    }, this.autoplayInterval);
  }

  /**
   * Stop manual autoplay control
   */
  stopManualAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  /**
   * Restart autoplay and progress bar after manual interaction
   */
  restartAutoplayAndProgress() {
    // Stop current progress bar
    this.stopProgressBar();

    // Reset current progress bar to 0%
    if (this.currentProgressBar) {
      const fill = this.currentProgressBar.querySelector('.progress-bar-fill');
      if (fill) {
        fill.style.width = '0%';
        fill.style.transition = 'none';
      }
    }

    // Restart manual autoplay if using manual control
    if (this.useManualAutoplay) {
      this.startManualAutoplay();
    } else {
      // Try to restart BlazeSlider's autoplay
      if (this.sliderElement) {
        if (typeof this.sliderElement.restartAutoplay === 'function') {
          this.sliderElement.restartAutoplay();
        } else if (typeof this.sliderElement.pauseAutoplay === 'function' && typeof this.sliderElement.startAutoplay === 'function') {
          this.sliderElement.pauseAutoplay();
          setTimeout(() => {
            this.sliderElement.startAutoplay();
          }, 50);
        }
      }
    }

    // Restart progress bar animation
    setTimeout(() => {
      this.startProgressBar();
    }, 100);
  }

  /**
   * Initialize progress bars
   */
  initializeProgressBars() {
    this.progressBars = Array.from(this.querySelectorAll('.progress-bar-item'));
    
    // Add click handlers to progress bars
    this.progressBars.forEach((bar) => {
      const slideIndex = parseInt(bar.getAttribute('data-slide-index')) || 0;
      
      bar.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (this.sliderElement) {
          // Mark as manual interaction
          this.isManualInteraction = true;
          
          // Get current slide index
          const currentIndex = this.currentSlideIndex || 0;
          const targetIndex = slideIndex;
          
          // If already on target slide, do nothing
          if (currentIndex === targetIndex) {
            return;
          }
          
          // Try multiple BlazeSlider methods to navigate
          let navigated = false;
          
          // Method 1: Try direct navigation methods
          if (typeof this.sliderElement.toSlide === 'function') {
            this.sliderElement.toSlide(targetIndex);
            navigated = true;
          } else if (typeof this.sliderElement.goToSlide === 'function') {
            this.sliderElement.goToSlide(targetIndex);
            navigated = true;
          } else if (typeof this.sliderElement.navigateTo === 'function') {
            this.sliderElement.navigateTo(targetIndex);
            navigated = true;
          }
          // Method 2: Try to access track and move directly
          else {
            const track = this.querySelector('.blaze-track');
            const slides = this.querySelectorAll('.blaze-slide');
            
            if (track && slides[targetIndex]) {
              // Calculate slide width including gap
              const slideWidth = slides[0].offsetWidth || 0;
              const gap = 0; // No gap in banner carousel
              const totalWidth = slideWidth + gap;
              
              // Calculate target position
              const targetPosition = -(targetIndex * totalWidth);
              
              // Move track directly
              track.style.transform = `translateX(${targetPosition}px)`;
              track.style.transition = 'transform 0.3s ease';
              
              // Update current index immediately
              this.currentSlideIndex = targetIndex;
              navigated = true;
              
              // Trigger onSlide callback manually if available
              if (typeof this.sliderElement.onSlide === 'function') {
                // The onSlide callback will be triggered by BlazeSlider's internal mechanism
                // but we can manually update our state
                setTimeout(() => {
                  if (this.sliderElement && typeof this.sliderElement.onSlide === 'function') {
                    // Force update
                    this.currentSlideIndex = targetIndex;
                  }
                }, 50);
              }
            }
            // Method 3: Fallback to next/prev navigation
            else if (typeof this.sliderElement.next === 'function' && typeof this.sliderElement.prev === 'function') {
              const steps = Math.abs(targetIndex - currentIndex);
              const direction = targetIndex > currentIndex ? 'next' : 'prev';
              
              // Navigate step by step with proper timing
              let stepCount = 0;
              const navigateStep = () => {
                if (stepCount < steps) {
                  if (direction === 'next') {
                    this.sliderElement.next();
                  } else {
                    this.sliderElement.prev();
                  }
                  stepCount++;
                  if (stepCount < steps) {
                    setTimeout(navigateStep, 200);
                  } else {
                    navigated = true;
                  }
                }
              };
              
              navigateStep();
            }
          }
          
          // Update current slide index immediately
          this.currentSlideIndex = targetIndex;
          
          // Restart autoplay and progress bar after a short delay
          setTimeout(() => {
            this.restartAutoplayAndProgress();
          }, navigated ? 100 : 300);
        }
      });
    });
  }

  /**
   * Start progress bar animation for current slide
   */
  startProgressBar() {
    // Stop any existing animation
    this.stopProgressBar();

    // Ensure we have a valid slide index
    if (this.currentSlideIndex < 0 || this.currentSlideIndex >= this.progressBars.length) {
      this.currentSlideIndex = 0;
    }

    // Reset all progress bars
    this.progressBars.forEach((bar, index) => {
      bar.classList.remove('active', 'completed');
      const fill = bar.querySelector('.progress-bar-fill');
      if (fill) {
        fill.style.width = '0%';
        fill.style.transition = 'none';
      }
    });

    // Set active bar
    if (this.progressBars[this.currentSlideIndex]) {
      this.currentProgressBar = this.progressBars[this.currentSlideIndex];
      this.currentProgressBar.classList.add('active');
      
      const fill = this.currentProgressBar.querySelector('.progress-bar-fill');
      if (fill) {
        // Reset and start animation
        fill.style.width = '0%';
        fill.style.transition = 'none';
        
        // Use requestAnimationFrame to ensure transition is applied
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            fill.style.transition = `width ${this.autoplayInterval}ms linear`;
            fill.style.width = '100%';
          });
        });
      }
    }
  }

  /**
   * Stop progress bar animation
   */
  stopProgressBar() {
    if (this.currentProgressBar) {
      const fill = this.currentProgressBar.querySelector('.progress-bar-fill');
      if (fill) {
        // Get current width before stopping
        const computedStyle = window.getComputedStyle(fill);
        const currentWidth = parseFloat(computedStyle.width) || 0;
        const containerWidth = this.currentProgressBar.offsetWidth;
        const percentage = containerWidth > 0 ? (currentWidth / containerWidth) * 100 : 0;
        
        // Stop animation by removing transition
        fill.style.transition = 'none';
        fill.style.width = `${percentage}%`;
      }
    }
  }

  /**
   * Update progress bar when slide changes
   */
  updateProgressBar() {
    // Mark previous slide as completed
    if (this.currentProgressBar) {
      this.currentProgressBar.classList.remove('active');
      this.currentProgressBar.classList.add('completed');
      const fill = this.currentProgressBar.querySelector('.progress-bar-fill');
      if (fill) {
        fill.style.width = '100%';
        fill.style.transition = 'none';
      }
    }

    // Start new progress bar for the current slide
    this.startProgressBar();
  }

  /**
   * Cleanup when component is removed
   */
  disconnectedCallback() {
    this.stopManualAutoplay();
  }

  /**
   * Show fallback UI when BlazeSlider fails to load
   */
  showFallbackUI() {
    // Hide navigation controls
    const prevBtn = this.querySelector('.blaze-prev');
    const nextBtn = this.querySelector('.blaze-next');
    const pagination = this.querySelector('.blaze-pagination-banner-carrousel');
    
    if (prevBtn) prevBtn.style.display = 'none';
    if (nextBtn) nextBtn.style.display = 'none';
    if (pagination) pagination.style.display = 'none';

    // Show first slide only
    const slides = this.querySelectorAll('.blaze-slide');
    slides.forEach((slide, index) => {
      if (index > 0) {
        slide.style.display = 'none';
      }
    });

    console.warn('BannerCarrousel: Using fallback display (first slide only)');
  }
}

// Registra o custom element quando o DOM estiver pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    customElements.define('banner-carrousel', BannerCarrousel);
  });
} else {
  customElements.define('banner-carrousel', BannerCarrousel);
}
